import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const SEITE = 50;
const gebiet = (s) => (s || '').replace(/ae/g, 'ä').replace(/oe/g, 'ö').replace(/ue/g, 'ü').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const titel = (n) => n.headline || n.title || '—';
const datum = (v) => (v ? new Date(v).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function KanzleiWebsiteAktuelles() {
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [total, setTotal] = useState(0);
  const mehr = () => api.get('/news', { params: { limit: SEITE, offset: items.length } }).then((r) => setItems((x) => [...x, ...(r.data.items || [])]));
  const laden = useCallback(() => {
    api.get('/news', { params: { limit: SEITE } }).then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); }).catch(() => setItems([]));
    api.get('/admin/news/status').then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { laden(); }, [laden]);
  const aktualisieren = async () => {
    setBusy(true);
    try { await api.post('/admin/news/refresh'); toast({ title: 'Aktualisierung gestartet', description: 'Neue Artikel erscheinen in einigen Minuten.' }); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Fehlgeschlagen', variant: 'destructive' }); } finally { setBusy(false); }
  };
  const loeschen = async (n) => {
    if (!window.confirm(`Artikel „${titel(n)}“ von der Website entfernen?`)) return;
    try { await api.delete(`/admin/news/${n.id}`); setItems((x) => x.filter((i) => i.id !== n.id)); } catch (e) { toast({ title: 'Löschen fehlgeschlagen', variant: 'destructive' }); }
  };
  return (
    <div className="min-h-screen bg-[#f5f8fa]" data-testid="website-aktuelles">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] max-w-[1200px] mx-auto px-4 lg:px-8 pb-16">
        <div className="pt-10 flex flex-wrap items-end gap-3">
          <div className="flex-1"><h1 className="font-serif text-4xl sm:text-5xl text-[#1B3A5C]">Aktuelles</h1>
            <p className="text-sm text-[#34536b] mt-2" data-testid="website-aktuelles-status">{status ? `${status.total_articles} Artikel${status.last_refresh_at ? ` · zuletzt aktualisiert ${datum(status.last_refresh_at)}` : items?.[0] ? ` · neuester Artikel ${datum(items[0].published_at)}` : ''}` : ' '}</p></div>
          <button type="button" onClick={aktualisieren} disabled={busy} data-testid="website-aktuelles-refresh" className="h-10 px-4 rounded-[10px] bg-[#1B3A5C] text-white text-sm hover:bg-[#2A5280] disabled:opacity-50 inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />Jetzt aktualisieren</button>
        </div>
        <div className="mt-6 bg-white border border-gray-200 rounded-2xl divide-y divide-gray-100">
          {!items ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>
            : items.map((n) => (
              <div key={n.id} className="flex items-start gap-3 p-4 hover:bg-[#f5f8fa] transition-colors" data-testid={`website-news-${n.id}`}>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#1B3A5C]">{titel(n)}</p>
                  <p className="text-xs text-gray-500">{[gebiet(n.rechtsgebiet), n.source, datum(n.published_at)].filter(Boolean).join(' · ')}</p></div>
                {(n.source_url || n.url) && <a href={n.source_url || n.url} target="_blank" rel="noreferrer" className="p-1.5 text-gray-400 hover:text-[#b8893d]" aria-label="Quelle öffnen"><ExternalLink className="w-4 h-4" /></a>}
                <button type="button" onClick={() => loeschen(n)} className="p-1.5 text-gray-400 hover:text-rose-600" aria-label="Entfernen" data-testid={`website-news-loeschen-${n.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          {items && items.length < total && <button type="button" onClick={mehr} data-testid="website-aktuelles-mehr" className="w-full py-3 text-xs tracking-wider text-[#b8893d] hover:bg-[#f5f8fa]">{`${SEITE} weitere laden (${total - items.length} übrig)`}</button>}
        </div>
      </main>
      <Toaster />
    </div>
  );
}
